// @ts-check
import React, { useState } from "react"
import TabView from "../../../components/atoms/tabView/TabView.atom"
import TabPanel from "../../../components/atoms/tabView/TabPanel.atom"
import RegisteredTasks from "./RegisteredTasks.controller"
import IndividualReportControls from "./IndividualReportControls.controller"
import IndividualReportSummary from "./IndividualReportSummary.controller"
import { getLastIndividualTeamMemberReport } from "../../../services/report.service"
import useIndividualReportFiltersContext from "../../../hooks/contextConsumers/useIndividualReportFiltersContext.hook"
import { useSession } from "../../../hooks/auth/useSession.hook"
import { useLocation } from "react-router-dom"
import { thisWeekRange } from "../../../utils/date.utils"
import { UNIFIEDDATE_VALUES } from "../../../consts/CompoundRangeDatePicker"
import { isObjectEmpty } from "../../../utils/object.utils"
import { DateTime } from "luxon"
import useMount from "../../../hooks/useMount"
import { getTeamMemberInfoWithUID } from "../../../services/teamMember.service"

/** @typedef {import("../../../routers/Navigation/IndividualReportLink").PropTypes} IndividualReportLinkProps */

/**
 * @param {{
 *   setCurrentTab: React.Dispatch<React.SetStateAction<number>>,
 *   openCreateTaskForTeamMemberModal: boolean,
 *   setOpenCreateTaskForTeamMemberModal: React.Dispatch<React.SetStateAction<boolean>>,
 *   registeredTasksRef: React.RefObject<import("./table/IndividualReportTableContainer.controller.types").IndividualReportTableContainerRef | null>
 * }} props
 * @returns
 */
const IndividualReportContent = (props) => {
  const {
    setCurrentTab,
    openCreateTaskForTeamMemberModal,
    setOpenCreateTaskForTeamMemberModal,
    registeredTasksRef,
  } = props

  const { teamMember, period, setPeriod, dateRange, setDateRange } =
    useIndividualReportFiltersContext()
  const [stateObtained, setStateObtained] = useState(false)
  const [highlightedTask, setHighlightedTask] = useState(
    /** @type {null | number} */ (null)
  )
  const [loadingTasks, setLoadingTasks] = useState(false)
  const [loadingSummary, setLoadingSummary] = useState(false)
  const { user } = useSession()
  const [activeIndex, setActiveIndex] = useState(0)

  /** @type {IndividualReportLinkProps} */
  const { state } = useLocation()

  /** @param {{ index: number }} e */
  const handleTabChange = (e) => {
    setActiveIndex(e.index)
    // navigate(tabs[e.index])
  }

  const getStateFromLocation = async () => {
    if (state?.taskId) {
      setHighlightedTask(state.taskId)
    }

    if (user.isTeamMember() && (!state || isObjectEmpty(state))) {
      const response = await getTeamMemberInfoWithUID(user.id)
      if (response.success && response.result) {
        teamMember.resetWith(response.result[0])
      } else {
        /**
         * NOTE: this is a fallback if the endpoint fails
         * this is missing data but it's better than nothing
         * !!! downloading the report will give the file an `undefined_undefined` filename !!!
         * !!! work anniversary calculations will be wrong !!!
         */
        // @ts-ignore
        teamMember.resetWith({
          id: user.id,
        })
      }
    } else {
      if (
        state?.teamMemberId &&
        state?.startDate &&
        state?.endDate &&
        state?.period
      ) {
        setDateRange([
          DateTime.fromISO(state.startDate),
          DateTime.fromISO(state.endDate),
        ])
        setPeriod(
          /** @type {UNIFIEDDATE_VALUES[keyof UNIFIEDDATE_VALUES]} */
          (state.period)
        )

        const response = await getTeamMemberInfoWithUID(state.teamMemberId)
        if (response.success && response.result) {
          teamMember.resetWith(response.result[0])
        } else {
          /**
           * NOTE: this is a fallback if the endpoint fails
           * this is missing data but it's better than nothing
           * !!! downloading the report will give the file an `undefined_undefined` filename !!!
           * !!! work anniversary calculations will be wrong !!!
           */
          // @ts-ignore
          teamMember.resetWith({
            id: state.teamMemberId,
          })
        }
      } else {
        gettingLastTeamMemberReport()
      }
    }

    setStateObtained(true)
  }

  useMount(getStateFromLocation)

  const gettingLastTeamMemberReport = async () => {
    const response = await getLastIndividualTeamMemberReport()
    if (response.success && response.result) {
      teamMember.resetWith(response.result)
    }
  }

  if (!stateObtained) return null
  return (
    <div id="individual-report">
      <IndividualReportControls
        teamMember={teamMember.value}
        setTeamMember={teamMember.setValue}
        period={period}
        setPeriod={setPeriod}
        dateRange={dateRange}
        setDateRange={setDateRange}
        onReset={() => {
          const hasChangedTeamMember =
            teamMember.defaultValue?.id !== teamMember.value?.id
          if (hasChangedTeamMember) {
            teamMember.revert()
          }
          setPeriod(UNIFIEDDATE_VALUES.SINGLEWEEK)
          setDateRange(thisWeekRange())
        }}
        loadingTasks={loadingTasks}
        loadingSummary={loadingSummary}
      />
      <div className="mobile-tab-view-nav-button">
        {activeIndex !== 0 && (
          <button
            className="p-tabview-nav-prev p-tabview-nav-btn p-link"
            type="button"
            onClick={() => handleTabChange({ index: activeIndex - 1 })}
          >
            <span className="pi pi-chevron-left" aria-hidden="true"></span>
          </button>
        )}
        {activeIndex === 0 && <span>Tareas registradas</span>}
        {activeIndex === 1 && <span>Resumen</span>}
        <span className="tabview-underline" />
        {activeIndex !== 1 && (
          <button
            className="p-tabview-nav-next p-tabview-nav-btn p-link"
            type="button"
            onClick={() => handleTabChange({ index: activeIndex + 1 })}
          >
            <span className="pi pi-chevron-right" aria-hidden="true"></span>
          </button>
        )}
      </div>
      <TabView
        onBeforeTabChange={(e) => {
          setCurrentTab(e.index)
        }}
      >
        <TabPanel header="Tareas registradas">
          <RegisteredTasks
            ref={registeredTasksRef}
            teamMember={teamMember.value}
            dateRange={dateRange}
            period={period}
            openCreateTaskForTeamMemberModal={openCreateTaskForTeamMemberModal}
            setOpenCreateTaskForTeamMemberModal={
              setOpenCreateTaskForTeamMemberModal
            }
            highlightedTask={highlightedTask}
            loading={loadingTasks}
            setLoading={setLoadingTasks}
          />
        </TabPanel>
        <TabPanel header="Resumen">
          <IndividualReportSummary
            teamMember={teamMember.value}
            dateRange={dateRange}
            loading={loadingSummary}
            setLoading={setLoadingSummary}
          />
        </TabPanel>
      </TabView>
    </div>
  )
}

export default IndividualReportContent
